import React from 'react';
import { classNames } from '../../utils/compatibility';

export interface InstructorBadgeProps {
  /** Whether user is an instructor */
  isInstructor?: boolean;
  /** Display variant - icon (small circle) or label (text badge) */
  variant?: 'icon' | 'label';
  /** Size of the badge */
  size?: 'xs' | 'sm' | 'md' | 'lg' | number;
  /** Additional class name */
  className?: string;
  /** Whether to display with glow effect */
  glow?: boolean;
  /** Whether to animate badge */
  animated?: boolean;
}

/**
 * InstructorBadge component
 *
 * Displays a badge for instructors/coaches
 */
const InstructorBadge: React.FC<InstructorBadgeProps> = ({
  isInstructor = false,
  variant = 'label',
  size = 'md',
  className = '',
  glow = false,
  animated = false,
}) => {
  // Don't render if not an instructor
  if (!isInstructor) return null;

  // Icon variant - small circular badge with graduation cap icon
  if (variant === 'icon') {
    const iconSize = typeof size === 'number' ? size :
      size === 'xs' ? 14 :
      size === 'sm' ? 16 :
      size === 'md' ? 20 : 24;

    return (
      <span
        className={classNames(
          'inline-flex items-center justify-center rounded-full',
          'bg-blue-500',
          'text-white',
          glow && 'shadow-sm shadow-blue-400/30',
          animated && 'animate-pulse',
          'ml-1',
          className
        )}
        style={{
          width: `${iconSize}px`,
          height: `${iconSize}px`,
          minWidth: `${iconSize}px`,
        }}
      >
        <svg
          className="w-3/4 h-3/4"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
        </svg>
      </span>
    );
  }

  // Label variant - text badge
  const sizeClasses = typeof size === 'string' ? {
    xs: 'text-xs px-1 py-0.5',
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-0.5',
    lg: 'text-base px-3 py-1',
  }[size] : 'text-xs px-1.5 py-0.5';

  return (
    <span
      className={classNames(
        'inline-flex items-center justify-center rounded font-semibold',
        'bg-blue-500',
        'text-white uppercase tracking-wider',
        glow && 'shadow-md shadow-blue-400/30',
        animated && 'animate-pulse',
        'ml-1',
        sizeClasses,
        className
      )}
    >
      INSTRUCTOR
    </span>
  );
};

export default InstructorBadge;